import {
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { Close, Delete } from '@material-ui/icons'
import { Confirm, FlexHorizontal, Spacer, useToasts } from '@vestaboard/installables'
import { DealAssociationTabs, DealAssociationTabsEnum } from '../components/Tabs'
import React, { useState } from 'react'

import { CreateDealAssociationModal } from '../modals/CreateDealAssociation'
import InfiniteScroll from 'react-infinite-scroller'
import { SearchInput } from '../../../ui/SearchInput'
import { useDeleteDealAssociation } from '../hooks/useDeleteDealAssociation'
import { useListDealAssociations } from '../hooks/useListDealAssociations'

export const DealAssociations = () => {
  const [showCreateDealAssociationModal, setShowCreateDealAssociationModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string | null>('')
  const [isDeleting, setIsDeleting] = useState<{
    dealId: string
    emailAddress: string
  } | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const { data, fetchMore, refetch } = useListDealAssociations({
    input: {
      searchTerm: searchTerm || undefined
    }
  })
  const [deleteDealAssociationMutation] = useDeleteDealAssociation()
  const { addToast } = useToasts()

  return (
    <>
      <FlexHorizontal spaceBetween>
        <DealAssociationTabs activeTab={DealAssociationTabsEnum.DealAssociations} />
        <FlexHorizontal>
          <Button
            variant='outlined'
            color='primary'
            onClick={() => {
              setShowCreateDealAssociationModal(true)
            }}>
            Create Deal Association
          </Button>
          <Box width={14} />
          <SearchInput onSearch={setSearchTerm} value={searchTerm || ''} />
          {searchTerm ? (
            <>
              <Box width={7} />
              <ButtonBase onClick={() => setSearchTerm(null)}>
                <Close />
              </ButtonBase>
            </>
          ) : null}
        </FlexHorizontal>
      </FlexHorizontal>
      <Spacer size='large' />
      <Card>
        <InfiniteScroll
          pageStart={0}
          loadMore={async () => {
            if (isLoadingMore) {
              return
            }

            setIsLoadingMore(true)
            return await fetchMore({
              updateQuery(previousQueryResult, options) {
                const newRecords = options.fetchMoreResult?.listDealAssociations.dealAssociations || []
                const previousRecords = previousQueryResult.listDealAssociations.dealAssociations || []
                return {
                  listDealAssociations: {
                    nextCursor: options.fetchMoreResult?.listDealAssociations.nextCursor as string,
                    dealAssociations: [...previousRecords, ...newRecords],
                    __typename: previousQueryResult.listDealAssociations.__typename
                  }
                }
              },
              variables: {
                input: {
                  searchTerm: searchTerm || undefined,
                  cursor: data?.listDealAssociations.nextCursor || null
                }
              }
            }).then(res => {
              setIsLoadingMore(false)
              return res
            })
          }}
          hasMore={!!data?.listDealAssociations?.nextCursor}
          loader={
            <Box>
              <Spacer size='large' />
              <CircularProgress />
            </Box>
          }>
          <TableContainer>
            <Table>
              <TableHead>
                <TableCell>Deal ID</TableCell>
                <TableCell>Email Address</TableCell>
                <TableCell></TableCell>
              </TableHead>
              <TableBody>
                {(data?.listDealAssociations?.dealAssociations || []).map(association => (
                  <TableRow key={`${association.id}`}>
                    <TableCell>
                      <ButtonBase onClick={() => setSearchTerm(association.dealId)}>{association.dealId}</ButtonBase>
                    </TableCell>
                    <TableCell>
                      <ButtonBase onClick={() => setSearchTerm(association.emailAddress)}>
                        {association.emailAddress}
                      </ButtonBase>
                    </TableCell>
                    <TableCell>
                      <ButtonBase
                        onClick={() => {
                          setIsDeleting({
                            dealId: association.dealId,
                            emailAddress: association.emailAddress
                          })
                        }}>
                        <Delete />
                      </ButtonBase>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </Card>
      <CreateDealAssociationModal
        visible={showCreateDealAssociationModal}
        onClose={() => {
          setShowCreateDealAssociationModal(false)
        }}
        onSuccess={() => {
          refetch()
          setShowCreateDealAssociationModal(false)
        }}
      />
      <Confirm
        title='Delete Deal Association?'
        message='This cannot be undone'
        open={!!isDeleting}
        handleClose={() => {
          setIsDeleting(null)
        }}
        handleAccept={async () => {
          if (isDeleting) {
            const result = await deleteDealAssociationMutation({
              variables: {
                input: isDeleting
              }
            })

            if (result.data?.deleteDealAssociation?.__typename === 'DealAssociationV2') {
              addToast('Deal Association deleted successfully', {
                appearance: 'success',
                autoDismiss: true
              })
              await refetch()
            } else {
              addToast('Failed to delete Deal Association', {
                appearance: 'error',
                autoDismiss: true
              })
            }
          }
          setIsDeleting(null)
        }}
      />
    </>
  )
}
